/**
 * Tracks whether a held prebid bid is still billable.
 *
 * OpenRTB's `bid.exp` is the seconds the bidder tolerates between bid and
 * impression beacon (e.g. 300s on current AY test demand). After `exp`
 * elapses, the cached creative may still render — AY's cache TTL is longer
 * (10–30 min) — but the impression is non-billable. This gate is what
 * keeps the host-side render path from spending a stale bid.
 *
 * Style mirrors `PacingGate`: pure state machine, no side effects, all
 * timestamps injected so tests stay deterministic. The orchestrator
 * (host-shell) owns when to call `store()` (after a /ads/bid response) and
 * `evaluate()` / `consume()` (at render time). Telemetry emission for
 * stale-rebid lives in the orchestrator too — this class only answers
 * "is the held bid still fresh right now?".
 *
 * Scope today: rewarded video + video interstitial. Banner and display
 * interstitial render within seconds of the bid (no gap to bridge), so
 * they don't go through this gate.
 *
 * Note on usefulness today: the current code fetches the bid at show
 * time, milliseconds before passing to IMA — so the gate is in practice
 * always FRESH on its first check. This class is the scaffolding for the
 * follow-up bid-ahead change (fetch at `query_ready`, hold across the
 * delay between readiness gate and user tap). Wiring it now means that
 * follow-up is a one-line move of the `store()` call to the readiness
 * path — and the freshness contract is already tested and shipped.
 */
export const DEFAULT_BID_EXP_SECONDS = 240;
/** Floor on the safety margin in milliseconds. */
const MIN_SAFETY_MARGIN_MS = 30000;
export class BidFreshnessGate {
    constructor(options = {}) {
        this.held = null;
        // Clamp to [0, 1). Outside that range means "no margin"; we treat it
        // as zero rather than throwing — defensive against caller mistakes.
        const r = options.safetyMarginRatio;
        this.safetyMarginRatio = typeof r === 'number' && r >= 0 && r < 1 ? r : 0.2;
    }
    /**
     * Store a freshly-fetched bid. `expiresAt` is the server-pushed absolute
     * ISO timestamp from /ads/bid; the gate compares against `now` so the
     * client doesn't need a sync'd clock. A missing `expiresAt` falls back
     * to `(now + DEFAULT_BID_EXP_SECONDS * 1000)` so the gate still has a
     * deadline rather than treating absent metadata as "fresh forever".
     *
     * Calling `store()` replaces any previously-held bid. Caller-side rule:
     * only call this after a successful bid (hasBid === true) — empty-bid
     * cases should `clear()` instead.
     */
    store(targeting, expiresAt, now = Date.now()) {
        let expiresAtMs;
        if (expiresAt) {
            const parsed = Date.parse(expiresAt);
            if (Number.isFinite(parsed)) {
                expiresAtMs = parsed;
            }
        }
        // Backstop — if the server didn't send a parseable expiresAt, apply
        // the default. Better to have a deadline than no deadline.
        if (expiresAtMs === undefined) {
            expiresAtMs = now + DEFAULT_BID_EXP_SECONDS * 1000;
        }
        this.held = {
            targeting: Object.assign({}, targeting),
            expiresAt,
            expiresAtMs,
            fetchedAt: now,
        };
    }
    /**
     * Drop the held bid — call this when a bid is consumed at render time,
     * or when /ads/bid returned `hasBid: false`. After `clear()`, `has()`
     * returns false and `evaluate()` returns `'no_bid'`.
     */
    clear() {
        this.held = null;
    }
    /** True when there's a stored bid (regardless of freshness). */
    has() {
        return this.held !== null;
    }
    /**
     * Determine whether the held bid is still billable.
     *
     * Returns:
     *   `null`               — held bid is fresh, safe to render.
     *   `'no_bid'`           — gate is empty (caller must fetch).
     *   `'expired'`          — held bid past its deadline (caller must re-fetch).
     *   `'expired_unknown'`  — held bid has no parseable expiresAt; treated
     *                          as stale to avoid spending an undated bid.
     *
     * Pure — does not mutate state. Caller decides what to do with the
     * disposition (consume vs. clear+refetch).
     */
    evaluate(now = Date.now()) {
        const h = this.held;
        if (!h)
            return 'no_bid';
        if (h.expiresAtMs === undefined)
            return 'expired_unknown';
        const deadline = h.expiresAtMs - this.computeMarginMs(h);
        if (now >= deadline)
            return 'expired';
        return null;
    }
    /**
     * Inspect the held bid without removing it. Returns undefined when empty.
     * Useful for telemetry (`age_ms`) at the call site. To take the bid out
     * of the gate, use `consume()`.
     */
    peek() {
        if (!this.held)
            return undefined;
        return {
            targeting: Object.assign({}, this.held.targeting),
            expiresAt: this.held.expiresAt,
            fetchedAt: this.held.fetchedAt,
        };
    }
    /**
     * Atomically take the held bid out of the gate (clearing it). Returns
     * the stored targeting record, or `undefined` when the gate is empty.
     * Does NOT check freshness — the caller is responsible for evaluating
     * first. Designed so a consumer can do:
     *
     *   if (gate.evaluate() === null) renderWith(gate.consume()!);
     *   else { gate.clear(); const fresh = await fetchBid(); renderWith(fresh); }
     */
    consume() {
        const h = this.held;
        if (!h)
            return undefined;
        this.held = null;
        return h.targeting;
    }
    /**
     * Safety margin in ms for the held bid. Derived from the bid's own exp
     * window (= expiresAtMs - fetchedAt) so a short-exp bid (e.g. 60s) still
     * gets a proportional buffer; floored at MIN_SAFETY_MARGIN_MS so a 0s
     * fraction never produces a deadline equal to the absolute expiry.
     */
    computeMarginMs(h) {
        if (h.expiresAtMs === undefined)
            return MIN_SAFETY_MARGIN_MS;
        const total = h.expiresAtMs - h.fetchedAt;
        if (total <= 0)
            return MIN_SAFETY_MARGIN_MS;
        const fromRatio = Math.floor(total * this.safetyMarginRatio);
        return Math.max(fromRatio, MIN_SAFETY_MARGIN_MS);
    }
}
